import { fuzzyMatchLabel } from '@/util/fuzzyMatchLabel';

/** The picker fields the grouping reads. Structural, so `ComboboxOption` satisfies it. */
export type GroupableOption = {
  value: string;
  label: string;
  shortLabel?: string | null;
  synonyms?: string[];
  /** Value of the parent option, or null/undefined at the top level. */
  parentValue?: string | null;
};

export type GroupedRow<Option extends GroupableOption> = {
  option: Option;
  /** 0 for a top-level row, 1 for a subtype, and so on. */
  depth: number;
  /** Label of the parent row, when there is one. */
  parentLabel?: string;
  /** True when at least one subtype is in the options, so the row acts as a group header. */
  hasChildren: boolean;
};

function byLabel(first: GroupableOption, second: GroupableOption) {
  return first.label.localeCompare(second.label);
}

/**
 * Orders options as a tree: each parent followed by its subtypes, alphabetical by label within
 * every level, one indent step per depth. A parent that is not among the options leaves the
 * option top-level, so a partial list still renders.
 *
 * With a query, a row is kept when it matches (label, short label or synonym), when an
 * ancestor matches (a matching parent brings its subtypes), or when a descendant matches
 * (a matching subtype appears under its parent for context).
 */
export function groupOptions<Option extends GroupableOption>(
  options: Option[],
  query = ''
): GroupedRow<Option>[] {
  const known = new Set(options.map((option) => option.value));

  const children = new Map<string, Option[]>();

  const roots: Option[] = [];

  options.forEach((option) => {
    const parent = option.parentValue;

    if (parent && parent !== option.value && known.has(parent)) {
      children.set(parent, [...(children.get(parent) ?? []), option]);
    } else {
      roots.push(option);
    }
  });

  const trimmedQuery = query.trim();

  const kept = new Set<string>();

  const keepSubtree = (option: Option) => {
    if (kept.has(option.value)) {
      return;
    }

    kept.add(option.value);

    (children.get(option.value) ?? []).forEach(keepSubtree);
  };

  // True when this subtree holds a match. Keeps a matching option's whole subtree and the
  // ancestors on the way down to any match.
  const visit = (option: Option): boolean => {
    if (
      fuzzyMatchLabel(option.label, option.shortLabel ?? '', option.synonyms ?? [], trimmedQuery)
    ) {
      keepSubtree(option);

      return true;
    }

    const childMatched = (children.get(option.value) ?? []).map(visit).some((matched) => matched);

    if (childMatched) {
      kept.add(option.value);
    }

    return childMatched;
  };

  if (trimmedQuery) {
    roots.forEach(visit);
  }

  const rows: GroupedRow<Option>[] = [];

  const emit = (option: Option, depth: number, parentLabel: string | undefined) => {
    if (trimmedQuery && !kept.has(option.value)) {
      return;
    }

    const subtypes = [...(children.get(option.value) ?? [])].sort(byLabel);

    rows.push({ option, depth, parentLabel, hasChildren: subtypes.length > 0 });

    subtypes.forEach((child) => emit(child, depth + 1, option.label));
  };

  [...roots].sort(byLabel).forEach((root) => emit(root, 0, undefined));

  return rows;
}
